import * as React from "react";

interface UseCountUpOptions {
	duration?: number;
	startOnVisible?: boolean;
	formatNumber?: (value: number) => string;
}

export function useCountUp(value: number, options: UseCountUpOptions = {}): string {
	const { duration = 2000, startOnVisible = false, formatNumber } = options;
	const [count, setCount] = React.useState(0);
	const [hasStarted, setHasStarted] = React.useState(!startOnVisible);
	const elementRef = React.useRef<HTMLSpanElement>(null);

	React.useEffect(() => {
		if (!startOnVisible || hasStarted) return;

		const element = elementRef.current;
		if (!element) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setHasStarted(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 },
		);

		observer.observe(element);

		return () => observer.disconnect();
	}, [startOnVisible, hasStarted]);

	React.useEffect(() => {
		if (!hasStarted) return;

		const startTime = performance.now();
		const startValue = 0;
		const endValue = value;

		function animate(currentTime: number) {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);

			const easeOutCubic = (t: number): number => {
				return 1 - Math.pow(1 - t, 3);
			};

			const easedProgress = easeOutCubic(progress);
			const currentValue = Math.floor(
				startValue + (endValue - startValue) * easedProgress,
			);

			setCount(currentValue);

			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				setCount(endValue);
			}
		}

		requestAnimationFrame(animate);
	}, [value, duration, hasStarted]);

	const formattedValue = formatNumber
		? formatNumber(count)
		: count.toLocaleString("pt-BR");

	return formattedValue;
}

interface AnimatedNumberProps {
	value: number;
	duration?: number;
	formatNumber?: (value: number) => string;
	className?: string;
	prefix?: string;
	suffix?: string;
}

export function AnimatedNumber({
	value,
	duration = 1200,
	formatNumber,
	className,
	prefix = "",
	suffix = "",
}: AnimatedNumberProps) {
	const elementRef = React.useRef<HTMLSpanElement>(null);
	const [hasStarted, setHasStarted] = React.useState(false);

	React.useEffect(() => {
		if (hasStarted) return;

		const element = elementRef.current;
		if (!element) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setHasStarted(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 },
		);

		observer.observe(element);

		return () => observer.disconnect();
	}, [hasStarted]);

	const [count, setCount] = React.useState(0);

	React.useEffect(() => {
		if (!hasStarted) return;

		const startTime = performance.now();
		const startValue = 0;
		const endValue = value;

		function animate(currentTime: number) {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);

			const easeOutCubic = (t: number): number => {
				return 1 - Math.pow(1 - t, 3);
			};

			const easedProgress = easeOutCubic(progress);
			const currentValue = Math.floor(
				startValue + (endValue - startValue) * easedProgress,
			);

			setCount(currentValue);

			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				setCount(endValue);
			}
		}

		requestAnimationFrame(animate);
	}, [value, duration, hasStarted]);

	const formattedValue = formatNumber
		? formatNumber(count)
		: count.toLocaleString("pt-BR");

	return (
		<span ref={elementRef} className={className}>
			{prefix}
			{formattedValue}
			{suffix}
		</span>
	);
}
